/**
 * 顧客向けアプリの外枠。ナビと、いま見ているテナントの表示。
 *
 * CS 代行の帯は、**代行している本人が見ているときだけ**出す。事業者の人が普通に
 * 開いたときの初期表示に visumo 側の運用事情を持ち込まないためで、
 * デモのレビューで出た指摘をそのまま反映している。
 */

import { useEffect, useMemo, useState } from "react";
import {
  Layout,
  Menu,
  Typography,
  Tag,
  Flex,
  Drawer,
  Button as AntButton,
  Grid,
} from "antd";
import {
  MenuOutlined,
  ShopOutlined,
  PictureOutlined,
  AppstoreOutlined,
  VideoCameraOutlined,
  ProfileOutlined,
  DeploymentUnitOutlined,
  RocketOutlined,
  BuildOutlined,
  UserOutlined,
  TeamOutlined,
  HomeOutlined,
  NodeIndexOutlined,
  QuestionCircleOutlined,
  ExperimentOutlined,
  SendOutlined,
  RobotOutlined,
  ApiOutlined,
  CloudUploadOutlined,
  AuditOutlined,
  BellOutlined,
  SettingOutlined,
  ReadOutlined,
} from "@ant-design/icons";
import { useQuery } from "urql";
import { NotificationUnreadCountDocument } from "visumo-data-agent-web-shared";
import { NavLink, Outlet, useLocation } from "react-router";
import { Button, useSession } from "visumo-data-agent-web-shared";

import { DelegationBanner } from "./DelegationBanner";
import { useViewer } from "./ViewerContext";

/**
 * ナビのまとまり。**平積みにしない**。
 *
 * 画面が 10 を超えたあたりで、ひとつづきの並びからは「どれが同じ仕事か」が
 * 読めなくなった。仕事の単位で見出しを付け、見出しはリンクにしない
 * （押せない文字を押せるように見せない）。
 *
 * まとまりの中の並びは上から触る順。**空のまとまりは置かない**
 * （行き先が 1 つも無い見出しは、準備中の何かがあるように見える）。
 */
const NAV_GROUPS = [
  {
    heading: "いつも使う",
    items: [
      { to: "/", icon: <ShopOutlined />, label: "商品と投稿", end: true },

      { to: "/posts", icon: <PictureOutlined />, label: "投稿", end: false },
    ],
  },
  {
    heading: "商品知識",
    items: [
      {
        to: "/knowledge/coverage",
        icon: <ProfileOutlined />,
        label: "カバレッジ",
        end: false,
      },
      {
        to: "/video-meta",
        icon: <VideoCameraOutlined />,
        label: "動画メタ",
        end: false,
      },
      {
        to: "/knowledge/schema",
        icon: <DeploymentUnitOutlined />,
        label: "属性スキーマと統制語彙",
        end: false,
      },
      {
        to: "/knowledge/selling",
        icon: <RocketOutlined />,
        label: "売り方の提案",
        end: false,
      },
      {
        to: "/knowledge/regulation",
        icon: <BuildOutlined />,
        label: "レギュレーションブック",
        end: false,
      },
      { to: "/personas", icon: <UserOutlined />, label: "ペルソナ", end: true },
      {
        to: "/personas/coverage",
        icon: <TeamOutlined />,
        label: "ペルソナ別の充足",
        end: false,
      },
    ],
  },
  {
    heading: "AIO 対策",
    items: [
      {
        to: "/home",
        icon: <HomeOutlined />,
        label: "Context Coverage",
        end: true,
      },
      {
        to: "/products",
        icon: <AppstoreOutlined />,
        label: "商品一覧",
        end: false,
      },
      {
        to: "/aio",
        icon: <NodeIndexOutlined />,
        label: "AIO 対策（ハブ）",
        end: false,
      },
      {
        to: "/question-map",
        icon: <QuestionCircleOutlined />,
        label: "AI Question Map",
        end: false,
      },
      {
        to: "/rules",
        icon: <ExperimentOutlined />,
        label: "ルール",
        end: false,
      },
      { to: "/publish", icon: <SendOutlined />, label: "配信", end: false },
      {
        to: "/ai-connections",
        icon: <RobotOutlined />,
        label: "AI との連携",
        end: false,
      },
      {
        to: "/api",
        icon: <ApiOutlined />,
        label: "API キーと埋め込み",
        end: false,
      },
    ],
  },
  {
    heading: "設定",
    items: [
      {
        to: "/intake",
        icon: <CloudUploadOutlined />,
        label: "データの取り込み",
        end: false,
      },
      {
        to: "/settings",
        icon: <SettingOutlined />,
        label: "ログイン・権限",
        end: false,
      },
      { to: "/audit", icon: <AuditOutlined />, label: "監査ログ", end: false },
      {
        to: "/notifications",
        icon: <BellOutlined />,
        label: "通知",
        end: false,
      },
      // 見出しの横の「?」と同じものを、まとめて読むための行き先
      {
        to: "/help",
        icon: <ReadOutlined />,
        label: "ヘルプ",
        end: false,
      },
    ],
  },
];

export function AppShell() {
  const viewer = useViewer();
  const [mobileOpen, setMobileOpen] = useState(false);
  const screens = Grid.useBreakpoint();
  const session = useSession();
  const location = useLocation();
  const context = useMemo(
    () => ({ additionalTypenames: ["Notification", "NotificationConnection"] }),
    [],
  );
  const [notificationResult, refreshNotifications] = useQuery({
    query: NotificationUnreadCountDocument,
    context,
    pause: viewer.role === "CS_DELEGATE",
  });
  useEffect(() => {
    if (viewer.role !== "CS_DELEGATE")
      refreshNotifications({ requestPolicy: "network-only" });
  }, [location.pathname, viewer.role, refreshNotifications]);
  const unread = notificationResult.error
    ? null
    : notificationResult.data?.notifications;
  const unreadLabel =
    unread && (unread.unreadCountCapped || unread.unreadCount > 99)
      ? "99+"
      : unread && unread.unreadCount > 0
        ? String(unread.unreadCount)
        : null;
  const delegation = viewer.role === "CS_DELEGATE" ? viewer.delegation : null;

  const items = NAV_GROUPS.map((group) => ({
    type: "group" as const,
    label: group.heading,
    key: group.heading,
    children: group.items
      .filter(
        (item) => item.to !== "/notifications" || viewer.role !== "CS_DELEGATE",
      )
      .map((item) => ({
        key: item.to,
        icon: item.icon,
        label: (
          <NavLink
            to={item.to}
            end={item.end}
            onClick={() => setMobileOpen(false)}
          >
            {item.label}
            {item.to === "/notifications" && unreadLabel ? (
              <span aria-label={`未読 ${unreadLabel}件`}> {unreadLabel}</span>
            ) : null}
          </NavLink>
        ),
      })),
  }));
  const selected = NAV_GROUPS.flatMap((group) => group.items)
    .filter((item) =>
      item.end
        ? location.pathname === item.to
        : location.pathname.startsWith(item.to),
    )
    .sort((a, b) => b.to.length - a.to.length)[0]?.to;
  const navigation = (
    <nav aria-label="メインナビゲーション">
      <Menu
        mode="inline"
        selectedKeys={selected ? [selected] : []}
        items={items}
        style={{ border: 0 }}
      />
      <div style={{ padding: 20, borderTop: "1px solid #f0f0f0" }}>
        <Typography.Text strong>{viewer.tenant.label}</Typography.Text>
        <br />
        <Typography.Text type="secondary">
          {viewer.user.displayName}
        </Typography.Text>
        {viewer.delegation == null && viewer.role !== "CS_DELEGATE" ? (
          <div style={{ marginTop: 12 }}>
            <NavLink to="/tenants">会社を切り替える</NavLink>
          </div>
        ) : null}
        <Button
          variant="text"
          style={{ paddingLeft: 0 }}
          onClick={() => session.logout()}
        >
          ログアウト
        </Button>
      </div>
    </nav>
  );
  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/*
        代行の帯は**ナビゲーションの上**に、画面の幅いっぱいで出す。
        中身の区画に置くと、脇のナビと見出しの右に収まった 1 枚の札になり、
        「いまこの画面は visumo が代わりに触っている」という、画面全体に
        かかる断りに見えない。
      */}
      {delegation === null ? null : (
        <DelegationBanner
          actorLabel={delegation.actorLabel}
          expiresAt={delegation.expiresAt}
        />
      )}
      <Layout.Header className="studio-header">
        <Flex align="center" gap={12}>
          {!screens.lg && (
            <AntButton
              icon={<MenuOutlined />}
              aria-label="メニューを開く"
              onClick={() => setMobileOpen(true)}
            />
          )}
          <Typography.Text strong style={{ fontSize: 20 }}>
            Insight Studio
          </Typography.Text>
          <Tag color="volcano">商品インサイト</Tag>
        </Flex>
        <Typography.Text type="secondary">
          {viewer.tenant.label}
        </Typography.Text>
      </Layout.Header>
      <Layout>
        {screens.lg && (
          <Layout.Sider width={232} theme="light" className="studio-sidebar">
            {navigation}
          </Layout.Sider>
        )}
        <Drawer
          title="Insight Studio"
          placement="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          styles={{ body: { padding: 0 } }}
        >
          {navigation}
        </Drawer>
        <Layout.Content className="studio-content">
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <Outlet />
          </div>
        </Layout.Content>
      </Layout>
    </Layout>
  );
}
